import { InlineHelp } from "@/components/common/InlineHelp";
import { useAuth } from "@/contexts/AuthContext";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { seedOpsPaperwork, deleteOpsDeal } from "@/lib/opsPaperworkClient";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { OpsPaperworkTasksTab } from "./OpsPaperworkTasksTab";
import { OpsPaperworkNotificationsTab } from "./OpsPaperworkNotificationsTab";
import { OpsPaperworkDealsTab } from "./OpsPaperworkDealsTab";
import { OpsPaperworkTimelineTab } from "./OpsPaperworkTimelineTab";
import { OpsPaperworkSettingsTab } from "./OpsPaperworkSettingsTab";
import { OpsManufacturerPayablesTab } from "./OpsManufacturerPayablesTab";
import { OpsQbBooksTab } from "./OpsQbBooksTab";
import { WorkplaceCommsOversightPanel } from "./WorkplaceCommsOversightPanel";
import { useOpsPaperworkPanelModel } from "./useOpsPaperworkPanelModel";
import { OpsPaperworkTabs } from "./OpsPaperworkTabs";
import { OpsLocationsTab } from "./OpsLocationsTab";
import { OPS_PAPERWORK_UI_MANAGE_ROLES } from "@/lib/ops/opsPaperworkAccess";
import { OPS_REVIEWER_ROLES } from "@/lib/roles/turboOpsRoles";

export function OpsPaperworkPanel() {
  const auth = useAuth();
  const scopedOrgId = useScopedOrganizationId();
  const uid = auth.session?.user.id ?? "";
  const canManageOps = auth.hasRole(...OPS_PAPERWORK_UI_MANAGE_ROLES);
  const canReviewDeals = auth.hasRole(...OPS_REVIEWER_ROLES);
  const canOpenDeals = canManageOps || canReviewDeals;
  const m = useOpsPaperworkPanelModel({ auth, scopedOrgId, canManageOps, canReviewDeals, uid });

  // Container/padding doctrine \u00a7K1+K6: dropped the `max-w-4xl mx-auto` cap.
  // The shell + desktop layout pass own width.
  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "ops-paperwork-surface" }} className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Deals</h2>
          <InlineHelp label="About Deals" glyph="!">
            Deal checklists, inventory host webhook timeline, and org tasks. Evidence links go on checklist rows.
            Webhook: Settings tab generates a per-org URL (
            <code className="text-[11px]">/api/integrations/inventory-host/webhook?token=…</code>
            ).
          </InlineHelp>
        </div>
        {m.summary ? (
          <div className="text-xs text-gray-600 flex gap-3 dark:text-zinc-300">
            <span title="Unread in-app notifications">{"\ud83d\udd14"} {m.summary.unread}</span>
            <span
              {...{ [FIREFLY_TARGET_ATTR]: "ops-missing-paperwork" }}
              title="Overdue paperwork items"
            >
              {"\ud83d\udccb"} {m.summary.overdue}
            </span>
            <span title="Open tasks assigned to you">{"\u2705"} {m.summary.tasks}</span>
          </div>
        ) : null}
      </div>
      {!canOpenDeals ? <p className="text-xs text-indigo-900 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">Sales-safe view: tasks and notifications are available. Deal/template/settings management is hidden for your role.</p> : null}
      {m.msg ? <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{m.msg}</p> : null}
      <OpsPaperworkTabs
        tabs={m.visibleTabs}
        active={m.tab}
        unmappedLots={m.summary?.unmappedLots ?? 0}
        onSelect={(t) => {
          m.setTab(t);
          if (t !== "deals" && t !== "timeline" && t !== "payables") m.setSelectedId(null);
        }}
      />

      {canOpenDeals && m.tab === "deals" ? <OpsPaperworkDealsTab loading={m.loading} selectedId={m.selectedId} detail={m.detail} canManageSetup={canManageOps} newDealName={m.newDealName} setNewDealName={m.setNewDealName} setSelectedId={m.setSelectedId} onCreateDeal={m.onCreateDeal} onSeedTemplates={m.onSeedTemplates} onReseedDeal={async (id) => { await seedOpsPaperwork(id); await m.loadDetail(id); }} onDeleteDeal={async (id) => { await deleteOpsDeal(id); m.setSelectedId(null); await m.loadDeals(); }} onSavePaperRow={m.onSavePaperRow} canEditAttachments={canOpenDeals} onReloadDetail={async () => { if (m.selectedId) await m.loadDetail(m.selectedId); }} onOpenPayables={canManageOps ? () => m.setTab("payables") : undefined} salespersonBusy={m.salespersonBusy} onInviteSalesperson={() => void m.onInviteSalesperson()} onRequestSalespersonInvite={() => void m.onRequestSalespersonInvite()} onAddSalespersonLocation={() => void m.onAddSalespersonLocation()} typeMatchBusy={m.typeMatchBusy} onAssignDealType={m.onAssignDealType} onRematchDealType={m.onRematchDealType} /> : null}
      {canManageOps && m.tab === "locations" ? <OpsLocationsTab onChanged={m.refreshSummary} /> : null}
      {canManageOps && m.tab === "payables" ? (
        <OpsManufacturerPayablesTab deals={m.deals} presetDealId={m.selectedId} />
      ) : null}
      {canManageOps && m.tab === "books" ? <OpsQbBooksTab /> : null}
      {canOpenDeals && m.tab === "timeline" ? <OpsPaperworkTimelineTab selectedId={m.selectedId} events={m.events} /> : null}
      {m.tab === "tasks" ? <OpsPaperworkTasksTab uid={uid} auth={auth} scopedOrgId={scopedOrgId} taskAssigneeListError={m.taskAssigneeListError} taskAssigneeUseRawUuid={m.taskAssigneeUseRawUuid} setTaskAssigneeUseRawUuid={m.setTaskAssigneeUseRawUuid} taskAssigneeLoading={m.taskAssigneeLoading} taskAssigneeOptions={m.taskAssigneeOptions} newTaskAssignee={m.newTaskAssignee} setNewTaskAssignee={m.setNewTaskAssignee} newTaskTitle={m.newTaskTitle} setNewTaskTitle={m.setNewTaskTitle} newTaskBody={m.newTaskBody} setNewTaskBody={m.setNewTaskBody} onCreateTask={m.onCreateTask} tasksInbox={m.tasksInbox} tasksSent={m.tasksSent} onTaskAction={m.onTaskAction} /> : null}
      {m.tab === "notifications" ? <OpsPaperworkNotificationsTab notifications={m.notifications} onMarkAllRead={m.onMarkAllNotificationsRead} /> : null}
      {canManageOps && m.tab === "settings" && m.settings ? <OpsPaperworkSettingsTab settings={m.settings} setSettings={m.setSettings} setMsg={m.setMsg} onToggleSetting={m.onToggleSetting} onPatchUrl={m.onPatchUrl} onRotateInventoryHostWebhookToken={m.onRotateInventoryHostWebhookToken} webhookTokenBusy={m.webhookTokenBusy} onSeedRules={m.onSeedRules} onProbeStubs={m.onProbeStubs} onFastfieldSync={m.onFastfieldSync} fastfieldSubmissions={m.fastfieldSubmissions} fastfieldLoading={m.fastfieldLoading} /> : null}
      {canManageOps && m.tab === "oversight" ? (
        <WorkplaceCommsOversightPanel
          organizationId={scopedOrgId ?? ""}
          onNavigate={(link) => {
            if (link.deal_id) {
              m.setTab("deals");
              m.setSelectedId(link.deal_id);
              m.setMsg(`Opened deal ${link.deal_id.slice(0, 8)}…`);
              return;
            }
            if (link.lead_id) {
              m.setMsg(`Lead ${link.lead_id} — switch to Leads mode and open from recent list (UUID copied in browser console).`);
              console.info("[oversight] open lead", link.lead_id);
              return;
            }
            if (link.thread_id) {
              m.setMsg(`Thread ${link.thread_id.slice(0, 8)}… — open Workplace Messages from the header bell.`);
              return;
            }
            if (link.task_id) {
              m.setTab("tasks");
              m.setMsg(`Task ${link.task_id.slice(0, 8)}… — see Tasks tab.`);
            }
          }}
        />
      ) : null}
    </div>
  );
}
